import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Settings, FileText, Download, CalendarDays, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { getPhotoUrl } from '@/utils/photoUrl';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (value === undefined || value === null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return 'Inicio';
  return format(new Date(dateString), "dd 'de' MMM, yyyy HH:mm", { locale: es });
};

const CierresAdjudicacion = () => {
  const { user } = useAuth();
  const [cierres, setCierres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configuracion, setConfiguracion] = useState(0.5);

  // Modals state
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [newConfigValue, setNewConfigValue] = useState(0.5);

  const [showFacturaDialog, setShowFacturaDialog] = useState(false);
  const [selectedCierre, setSelectedCierre] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [cierresRes, configRes] = await Promise.all([
        axios.get(`${API}/cierres`),
        axios.get(`${API}/configuraciones/comision_adjudicacion`)
      ]);

      setCierres(cierresRes.data);
      setConfiguracion(configRes.data.valor);
    } catch (error) {
      console.error('Error cargando datos de cierres:', error);
      toast.error('No se pudieron cargar los cierres o configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async () => {
    try {
      await axios.put(`${API}/configuraciones/comision_adjudicacion`, { valor: parseFloat(newConfigValue) });
      setConfiguracion(newConfigValue);
      setShowConfigDialog(false);
      toast.success('Porcentaje de comisión actualizado');
    } catch (error) {
      console.error('Error actualizando configuración:', error);
      toast.error('Error al actualizar el porcentaje');
    }
  };

  const handleGenerarCierre = async () => {
    try {
      const res = await axios.post(`${API}/cierres`, {});
      if (res.data.cantidad > 0) {
        toast.success(`Cierre generado exitosamente con ${res.data.cantidad} publicaciones.`);
        loadData();
      } else {
        toast.info('No hay publicaciones adjudicadas pendientes de facturar en este momento.');
      }
    } catch (error) {
      console.error('Error generando cierre:', error);
      toast.error('Error al generar el cierre de adjudicaciones');
    }
  };

  const handleViewFactura = async (id) => {
    try {
      const res = await axios.get(`${API}/cierres/${id}`);
      setSelectedCierre(res.data);
      setShowFacturaDialog(true);
    } catch (error) {
      console.error('Error cargando detalle de cierre:', error);
      toast.error('Error al cargar los detalles de la factura');
    }
  };

  const generatePDF = async () => {
    if (!selectedCierre) return;

    const doc = new jsPDF();

    try {
      const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/uploads/logo.png`;
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = logoUrl;
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
      if (img.complete && img.naturalWidth > 0) {
        doc.addImage(img, 'PNG', 14, 10, 40, 15);
      }
    } catch (e) { }

    doc.setFontSize(16);
    doc.text("Plaza - Factura de Cierre", 14, 35);

    doc.setFontSize(10);
    doc.text(`ID del Cierre: ${selectedCierre.id}`, 14, 45);
    doc.text(`Generado: ${formatDate(selectedCierre.fechaCierre)}`, 14, 50);
    doc.text(`Período: ${formatDate(selectedCierre.periodoDesde)} - ${formatDate(selectedCierre.periodoHasta)}`, 14, 55);
    doc.text(`Comisión Aplicada: ${selectedCierre.porcentajeComision}%`, 14, 60);

    const tableColumn = ["Vehículo", "Lote", "KMs", "Adjudicado", "Comisión"];
    const tableRows = [];

    selectedCierre.items?.forEach(item => {
      tableRows.push([
        `${item.tipoGanado} ${item.categoria} ${item.raza || ''}`.trim(),
        item.numeroLote,
        item.cantidad?.toLocaleString() || '',
        formatCurrency(item.precioAdjudicacion),
        formatCurrency(item.comisionGenerada)
      ]);
    });

    autoTable(doc, {
      startY: 65,
      head: [tableColumn],
      body: tableRows,
      headStyles: { fillColor: [41, 128, 185] },
      rowPageBreak: 'avoid',
      styles: { minCellHeight: 10, valign: 'middle' }
    });

    const finalY = doc.lastAutoTable.finalY || 65;

    doc.setFontSize(11);
    doc.text(`Total Adjudicado: ${formatCurrency(selectedCierre.totalAdjudicado)}`, 14, finalY + 10);
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text(`Total Comisión: ${formatCurrency(selectedCierre.totalComision)}`, 14, finalY + 18);

    doc.save(`Factura_Cierre_${selectedCierre.id.substring(0, 8)}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Cierres de Adjudicaciones</h2>
          <p className="text-muted-foreground mt-2">
            Gestiona la facturación y visualiza los cierres de lotes adjudicados.
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {user?.rol === 'SuperAdmin' && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setNewConfigValue(configuracion);
                  setShowConfigDialog(true);
                }}
              >
                <Settings className="w-4 h-4 mr-2" />
                Configuración ({configuracion}%)
              </Button>
              <Button onClick={handleGenerarCierre} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                Generar Cierre Actual
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Historial de Cierres</CardTitle>
          <CardDescription>
            Listado de todas las facturaciones emitidas en el sistema.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : cierres.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
              <h3 className="mt-4 text-lg font-medium">No hay cierres emitidos</h3>
              <p className="text-muted-foreground">Genera el primer cierre para facturar los lotes adjudicados.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-muted-foreground uppercase bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 rounded-tl-lg">Fecha de Emisión</th>
                    <th className="px-4 py-3">Período</th>
                    <th className="px-4 py-3">Lotes</th>
                    <th className="px-4 py-3">Total Adjudicado</th>
                    <th className="px-4 py-3">Comisión Generada</th>
                    <th className="px-4 py-3 rounded-tr-lg text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cierres.map((cierre) => (
                    <tr key={cierre.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">
                        {formatDate(cierre.fechaCierre)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatDate(cierre.periodoDesde)} - {formatDate(cierre.periodoHasta)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary">{cierre.items?.length || 0}</Badge>
                      </td>
                      <td className="px-4 py-3 text-green-600 font-medium">
                        {formatCurrency(cierre.totalAdjudicado)}
                      </td>
                      <td className="px-4 py-3 font-bold">
                        {formatCurrency(cierre.totalComision)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleViewFactura(cierre.id)}>
                          <FileText className="w-4 h-4 mr-2" />
                          Ver Detalle
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Configuración de Comisión</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              Porcentaje de Comisión (%)
            </label>
            <Input
              type="number"
              step="0.01"
              value={newConfigValue}
              onChange={(e) => setNewConfigValue(e.target.value)}
              placeholder="Ej. 0.5"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Este valor se utilizará para calcular la comisión de los próximos cierres que se emitan, y también actualizará el cálculo del indicador "Renta generada" en el Dashboard para el período corriente.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdateConfig}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Factura/Recibo Dialog */}
      <Dialog open={showFacturaDialog} onOpenChange={setShowFacturaDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center pr-6">
              <span>Recibo de Cierre de Adjudicaciones</span>
              <Button variant="outline" size="sm" onClick={generatePDF}>
                <Download className="w-4 h-4 mr-2" /> Descargar PDF
              </Button>
            </DialogTitle>
          </DialogHeader>

          {selectedCierre && (
            <div className="overflow-y-auto pr-2 mt-4 space-y-6">
              <div className="flex justify-between items-start bg-muted/30 p-4 rounded-lg border">
                <div>
                  <h3 className="font-bold text-lg">Plaza</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Cierre Generado: {formatDate(selectedCierre.fechaCierre)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Período: {formatDate(selectedCierre.periodoDesde)} - {formatDate(selectedCierre.periodoHasta)}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">ID del Cierre</div>
                  <div className="font-mono text-xs">{selectedCierre.id}</div>
                  <div className="mt-2 text-sm">
                    Comisión Aplicada: <span className="font-bold">{selectedCierre.porcentajeComision}%</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3 border-b pb-2">Detalle de Lotes Adjudicados</h4>
                <div className="space-y-3">
                  {selectedCierre.items?.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted/20">
                      <div className="w-16 h-12 rounded overflow-hidden bg-muted flex-shrink-0">
                        {item.foto ? (
                          <img src={getPhotoUrl(item.foto)} alt="Vehículo" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Sin foto</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{item.tipoGanado} {item.categoria} {item.raza}</div>
                        <div className="text-xs text-muted-foreground">
                          Lote: {item.numeroLote} • {item.cantidad?.toLocaleString()} cantidad
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-muted-foreground">Adjudicado en</div>
                        <div className="font-medium text-green-600">{formatCurrency(item.precioAdjudicacion)}</div>
                      </div>
                      <div className="text-right flex-shrink-0 w-24">
                        <div className="text-xs text-muted-foreground">Comisión</div>
                        <div className="font-bold">{formatCurrency(item.comisionGenerada)}</div>
                      </div>
                    </div>
                  ))}

                  {(!selectedCierre.items || selectedCierre.items.length === 0) && (
                    <div className="text-center py-4 text-muted-foreground text-sm">No hay lotes en este cierre</div>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Adjudicado:</span>
                    <span className="font-medium">{formatCurrency(selectedCierre.totalAdjudicado)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold bg-muted p-2 rounded-lg">
                    <span>Total Comisión:</span>
                    <span>{formatCurrency(selectedCierre.totalComision)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CierresAdjudicacion;
